import {
  Component,
  Input,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  forwardRef,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  LOCALE_ID,
  Inject,
  HostListener,
} from '@angular/core';
import { FileListService } from '@schoolbelle/api/file';
import { froalaOptions } from './froala/froala.options';
import './froala/froala.save.extension';
import './froala/froala.fontsize.extension';

import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { filter, map } from 'rxjs/operators';
import { TextEditorService } from './text-editor.service';
import { FroalaEditorDirective } from 'angular-froala-wysiwyg';
import { forEach, set } from 'lodash-es';
import { TranslationService } from '@schoolbelle/common/translation';
import { TranslationEditorComponent } from '../translation-editor/translation-editor.component';
import { ToastrService } from 'ngx-toastr';
import { DatePipe } from '@angular/common';
import { BoardService } from '@schoolbelle/api/board';

export const TextEditorComponentValueAccessor: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => TextEditorComponent),
  multi: true
};
@Component({
  selector: 'app-text-editor',
  templateUrl: './text-editor.component.html',
  styleUrls: ['./text-editor.component.scss'],
  providers: [
    TextEditorComponentValueAccessor,
    // TextEditorService, // original :: not commented
    DatePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TextEditorComponent extends TranslationEditorComponent {
  
  // double declaration due to build error
  @Input() set lang(v:string) {
    if (this._lang === v) return;
    this._lang = v;
    if (this.inited) this.cdRef.detectChanges();
  }
  @Input() usedLangs:string[] = [];
  @Input() placeholder:string;
  // double declaration due to build error


  @ViewChild(FroalaEditorDirective) froalaEditorDirective:FroalaEditorDirective;
  @Input() filelist:FileListService = new FileListService();

  // Caution!! this variables are "TOO" dependent on HTML element!
  arrParentElem:any;
  parentElemType:string;

  froalaOptions:any;
  defaultOptions = {
    ...froalaOptions, 
    events: {
      'froalaEditor.initialized':(e:Event, editor:any)=>{
        this.froalaEditor = editor;
        editor.events.on('drop', (e) => {
          const file:File = e.originalEvent.dataTransfer.files[0];
          if (file.type.match(/video/i)) {
            alert(this.translation.get('text-editor-drag-and-drop-video-alert'));
            e.preventDefault();
            return false;
          }

          //
          if(this.parentElemType === 'media' && !file.type.match(/video/i) && !file.type.match(/image/i)) {
            alert(this.translation.get('text-editor-drag-and-drop-image-accept-alert'));
            e.preventDefault();
            return false;
          }
        }, true);

        this.textEditor.editor = this.froalaEditor;
      },
      'froalaEditor.image.beforeUpload': (e: Event, editor: any, files: FileList) => {
        this.textEditor.insertImage(editor, this.filelist, files[0]);
        return false;
      },
      'froalaEditor.file.beforeUpload': (e: Event, editor: any, files: FileList) => {
        this.textEditor.insertLink(editor, this.filelist, files[0]);
        return false;
      },
      'froalaEditor.video.beforeUpload': (e: Event, editor: any, files: FileList) => {
        this.textEditor.insertVideo(editor, this.filelist, files[0]);
        return false;
      },
      'froalaEditor.image.inserted': (e, editor, $img) => {
        this.textEditor.assignIdToInsertedFileElement($img[0]);
        this.cdRef.detectChanges();
      },
      'froalaEditor.file.inserted': (e: Event, editor: any, $a: any) => {
        const a: HTMLAnchorElement = $a[0];
        // undo sanitization for objectURL to work
        a.href = decodeURIComponent(a.href); 
        this.textEditor.assignIdToInsertedFileElement(a);
        this.cdRef.detectChanges();
      },
      'froalaEditor.video.inserted': (e, editor, $container) => {
        this.textEditor.assignIdToInsertedFileElement($container[0]);
        this.cdRef.detectChanges();
      },
      'froalaEditor.image.removed': (e: Event, editor: any, $el: any) => {
        const img: HTMLImageElement = $el[0];
        const url = img.src;
        this.deleteFromFilelist(url);
      },
      'froalaEditor.file.unlink': (e: Event, editor: any, el: HTMLAnchorElement) => {
        const url = el.href;
        this.deleteFromFilelist(url);
      },
      'froalaEditor.video.removed': (e: Event, editor: any, $container: any) => {
        const container:HTMLSpanElement = $container[0];
        const iframe = container.querySelector('iframe');
        if (iframe) this.deleteFromFilelist(iframe.src);
      },
      'froalaEditor.custom.save': (e, editor) =>{ 
        this.clickSave.next(null);
      },
      'froalaEditor.custom.load': (e, editor) =>{ 
        this.clickLoad.next(null);
      },
      'froalaEditor.input': () => {
        this.cdRef.detectChanges();
      },

      //prevent copy elements without selection area
      'froalaEditor.copy': (ev) => {
        const selectedText = this.froalaEditor.selection.text();

        if(selectedText.length === 0) {
          // this.isCopiedEmpty = true;
          ev.preventDefault();
          ev.stopPropagation();
          this.toastr.error(this.translation.get("no-data-to-copy"));
          return false;
        } else {
          return ev;
        }
      },

      // day print fullscreen
      'froalaEditor.commands.after': (cmd, p1, p2) => {
        const boardType = this.board.currentBoard.type;

        if(boardType === 'classnews' && p2 === 'fullscreen') {
          const isFullScreen = this.froalaEditor.fullscreen.isActive();
          const elemToday = this.elementRef.nativeElement.querySelector('div.fullscreen-today');

          if(isFullScreen) {
            elemToday.classList.remove('hide');
          } else {
            elemToday.classList.add('hide');
          }

          this.cdRef.detectChanges();
        }
      }
    }
  };
  @Input('froalaOptions') set customFroalaOptions (custom:any) {
    this.froalaOptions = {
      ...this.froalaOptions,
      ...this.defaultOptions,
      ...custom
    };
    // placeholder binding hot fix
    if (this.froalaEditor) {
      this.froalaEditor.opts.placeholderText = this.froalaOptions.placeholderText;
        this.froalaEditor.placeholder.refresh();
    }  
    // placeholder binding hot fix
  };
  @Output() clickSave = new EventEmitter(); 
  @Output() clickLoad = new EventEmitter(); 

  private froalaEditor:any;
  
  
  constructor(
    elementRef:ElementRef,
    cdRef: ChangeDetectorRef,
    private toastr: ToastrService,
    @Inject(LOCALE_ID) locale_id:string, 
    private translation:TranslationService,
    private textEditor:TextEditorService,
    private datePipe : DatePipe,
    private board: BoardService,
  ) {
    super(elementRef, cdRef, locale_id);
    // this.toastr = toastr;
  }
  ngOnInit() {
    super.ngOnInit();

    //
    this.arrParentElem = this.elementRef.nativeElement.parentElement.parentElement.tagName.toLowerCase().split('-');
    this.parentElemType = this.arrParentElem[this.arrParentElem.length-1];

    this.filelist.onChange.pipe(
      filter(e=>e.type === 'remove'),
      map(e=>e.data)
    ).subscribe(removed=>{
      removed.forEach(file=>{
        this.textEditor.deleteFileElementInEditor(this.froalaEditor, file)
      })
    });
    this.filelist.onChange.pipe(
      filter(e=>e.type === 'change'),
      map(e=>e.data),
    ).subscribe(([from, to])=>{
      this.textEditor.updateFileElementInEditor(this.froalaEditor, from, to)
    })
  }
  insertIntoFilelist(url: string, file: File) {
    this.textEditor.insertFileIntoFilelist(this.filelist, url, file);
    this.cdRef.detectChanges();
  }
  deleteFromFilelist(url) {
    this.textEditor.deleteFileFromFilelist(this.filelist, url);
    this.cdRef.detectChanges();
  }

  insertClassNewsTitle () {
    // if board name is classnews
    const boardType = this.board.currentBoard.type;

    if(boardType === 'classnews'){
      // setTimeout(()=>{
        const detectKR = ['ko', 'kr']
        let clsNewsTranslation;
        let today;
        
        if(detectKR.indexOf(this.locale_id.toLocaleLowerCase()) !== -1) {
          clsNewsTranslation = "알림장";
          today = `<div class="fullscreen-today hide">${this.datePipe.transform(new Date(), 'MMMM dd일 EEEE', this.locale_id)} ${clsNewsTranslation}</div>`;
        } else {
          clsNewsTranslation = boardType;
          today = `<div class="fullscreen-today hide">${this.datePipe.transform(new Date(), 'MMMM dd EEEE', this.locale_id)} ${clsNewsTranslation}</div>`;
        }

        
        this.elementRef.nativeElement.querySelector('div.fr-wrapper').insertAdjacentHTML('beforebegin', today);
      // }, 1);
    }

  }

  ngAfterViewInit () {
    forEach(this.elementRef.nativeElement.querySelectorAll("div[translation-text]"), (el) => this.translation.set(el.id,  el.attributes["translation-text"].value))
    this.updateButtonsText();

    // fullsize editor copy event
    document.getElementsByClassName('fr-element fr-view')[0].addEventListener('copy', this.onCopy);

    // fullsize date indicator append
    this.insertClassNewsTitle();
  }

  private updateButtonsText () {
    const locale = this.locale_id.slice(0,2);
    const targets = [{name:'save', title:'Temp Save'}, {name:'load', title:'Load'}];
    targets.forEach(target=>{
      set($.FE.LANGUAGE, [locale, 'translation', target.title], this.translation.get(`text-editor-${target.name}`))
      
      const buttonEl:HTMLElement = this.elementRef.nativeElement.querySelector(`[data-cmd=${target.name}]`);
      if (buttonEl) forEach(buttonEl.children, el=>{
        el.textContent = this.translation.get(`text-editor-${target.name}`);
      })
  
    })

  }

  detectMediaEditor(file:File) {
    const arrMedia = this.elementRef.nativeElement.parentElement.parentElement.tagName.toLowerCase().split('-');
    const screenType = arrMedia[arrMedia.length-1];
    const isMediaFile = true; // detectMediaFile(file);

    return isMediaFile;
  }
  
  @HostListener('oncopy', ['$event'])
  onCopy(ev) {
    let clipboardData = ev.clipboardData || (<any>window).clipboardData;
    const selection = document.getSelection().toString();

    // no data copied
    if(selection.length === 0) {
      // console.log("stop!");
      ev.preventDefault();
      ev.stopPropagation();
      clipboardData.setData('Text', '');
      return false;
    } else {
      return ev;
    }
  }
}
